import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";
import { useSiteContent } from "../../lib/siteContent";

const EMPTY_BILINGUAL = { tr: "", en: "" };

export default function AdminContactInfo() {
  const { authedClient } = useAdminAuth();
  const { refresh: refreshSite } = useSiteContent();
  const [services, setServices] = useState([]);
  const [contact, setContact] = useState({
    address: { ...EMPTY_BILINGUAL },
    phone: "",
    email: "",
    workingHours: { ...EMPTY_BILINGUAL },
    instagramUrl: "",
    linkedinUrl: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient.get("/admin/site");
      setServices(data.services || []);
      const c = data.contact || {};
      setContact({
        address: { ...EMPTY_BILINGUAL, ...(c.address || {}) },
        phone: c.phone || "",
        email: c.email || "",
        workingHours: { ...EMPTY_BILINGUAL, ...(c.workingHours || {}) },
        instagramUrl: c.instagramUrl || "",
        linkedinUrl: c.linkedinUrl || "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await authedClient.put("/admin/site", { services, contact });
      toast.success("İletişim bilgileri kaydedildi");
      refreshSite();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-ink/55 text-sm py-12 text-center">Yükleniyor…</div>;

  return (
    <div data-testid="admin-contact-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">İletişim Bilgileri</h1>
          <p className="mt-1 text-sm text-ink/60">Footer ve İletişim sayfasında görünen iletişim bilgileri ve sosyal medya bağlantıları.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="contact-info-save" className="btn-primary disabled:opacity-60">
          <Save className="w-4 h-4" strokeWidth={1.5} />
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </header>

      <div className="surface-card p-7 md:p-9 space-y-6">
        <div>
          <span className="admin-label">Adres</span>
          <div className="grid md:grid-cols-2 gap-3">
            {["tr", "en"].map((lang) => (
              <div key={lang}>
                <span className="block text-[10px] uppercase tracking-widest text-ink/45 mb-1">{lang.toUpperCase()}</span>
                <textarea
                  data-testid={`contact-info-address-${lang}`}
                  rows={3}
                  value={contact.address[lang] || ""}
                  onChange={(e) => setContact((p) => ({ ...p, address: { ...p.address, [lang]: e.target.value } }))}
                  className="admin-textarea"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <span className="admin-label">Telefon</span>
            <input
              data-testid="contact-info-phone"
              value={contact.phone}
              onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))}
              className="admin-input"
              placeholder="+90 …"
            />
          </div>
          <div>
            <span className="admin-label">E-posta</span>
            <input
              data-testid="contact-info-email"
              type="email"
              value={contact.email}
              onChange={(e) => setContact((p) => ({ ...p, email: e.target.value }))}
              className="admin-input"
            />
          </div>
        </div>

        <div>
          <span className="admin-label">Çalışma Saatleri</span>
          <div className="grid md:grid-cols-2 gap-3">
            {["tr", "en"].map((lang) => (
              <div key={lang}>
                <span className="block text-[10px] uppercase tracking-widest text-ink/45 mb-1">{lang.toUpperCase()}</span>
                <input
                  data-testid={`contact-info-hours-${lang}`}
                  value={contact.workingHours[lang] || ""}
                  onChange={(e) => setContact((p) => ({ ...p, workingHours: { ...p.workingHours, [lang]: e.target.value } }))}
                  className="admin-input"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <span className="admin-label">Instagram URL</span>
            <input
              data-testid="contact-info-instagram"
              type="url"
              value={contact.instagramUrl}
              onChange={(e) => setContact((p) => ({ ...p, instagramUrl: e.target.value }))}
              className="admin-input"
              placeholder="https://instagram.com/…"
            />
          </div>
          <div>
            <span className="admin-label">LinkedIn URL</span>
            <input
              data-testid="contact-info-linkedin"
              type="url"
              value={contact.linkedinUrl}
              onChange={(e) => setContact((p) => ({ ...p, linkedinUrl: e.target.value }))}
              className="admin-input"
              placeholder="https://linkedin.com/company/…"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
